/**
 * @file Smart Page Action Button Editor
 * @description Action button related info is stored in both Datasource and
 * ComponentConfig of SiteConfig.
 * Check "getUpdatedComponentConfigByType" to see the data structure
 */
import { find } from 'underscore';
import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { Dropdown } from '@fluentui/react/lib/Dropdown';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SiteConfigContext, useIsSmartPageBookAppointmentEnabled } from '@bingads-webui-campaign-react/smart-page-context';
import {
  useDefaultEditorStyle,
  preventEventPropagation
} from '@bingads-webui-campaign-react/smart-component-util';

import { ActionCustomEditor } from './action-custom-editor';
import { getActionButtonProps } from './utils/get-action-button-props';
import {
  getUpdatedComponentConfigByType,
  getActionOptions,
} from './utils/smart-action-types';

const getStyles = (_, { palette = {}, iconFontFamily }) => ({
  group: {
    margin: '20px 0px',
  },
  previewLink: {
    color: palette.themePrimary || '#0078d4',
    background: 'none',
    border: 'none',
    paddingLeft: 0,
  },
  actionTitle: {
    display: 'flex',
  },
  actionInlineHelp: {
    display: 'inline',
    '& .helpbutton': {
      paddingTop: '2px',
      paddingLeft: '2px',
      '&::before': {
        content: '"\\E9CE"',
        fontFamily: iconFontFamily,
        position: 'relative',
        color: palette.themePrimary,
        verticalAlign: 'baseline',
        display: 'inline',
        lineHeight: 'inherit',
      },
    },
  },
});

// calloutProps are used to render inline Dropdown list.
const accessibilityProps = {
  calloutProps: { doNotLayer: true },
  styles: { callout: { zIndex: 1 } },
};

export const UnstyledSmartActionButtonEditor = ({
  classes,
  i18n,
  componentId,
}) => {
  const {
    titleClassName,
    descriptionClassName,
    editorContentClassName,
  } = useDefaultEditorStyle();
  const { siteConfig, dispatchSiteConfig } = useContext(SiteConfigContext);
  const isSmartPageBookAppointmentEnabled = useIsSmartPageBookAppointmentEnabled();

  const pilots = useMemo(() => ({
    isSmartPageBookAppointmentEnabled,
  }), [isSmartPageBookAppointmentEnabled]);
  const options = useMemo(() => getActionOptions(i18n), [i18n]);

  const ctaLabel = i18n.getString(_TL_('Customer action button'));

  const actionButtonProps = getActionButtonProps(siteConfig, componentId);
  const {
    componentConfig,
    actionType,
  } = actionButtonProps;

  const [selectedActionType, setSelectedActionType] = useState(find(
    options,
    item => item.key === actionType
  ));

  // Callback for selecting an option.
  const onActionTypeChange = (e, option) => {
    preventEventPropagation(e, false);

    const newComponentConfig = getUpdatedComponentConfigByType(
      option.key,
      componentConfig,
      i18n,
      pilots
    );

    setSelectedActionType(option);
    dispatchSiteConfig({
      type: 'UPDATE_COMPONENT_BY_ID',
      payload: newComponentConfig,
    });
  };

  return (
    <div className="smart-action-button-editor" >
      <h2 className={classNames(classes.actionTitle, titleClassName)}>
        {ctaLabel}
        <div className={classes.actionInlineHelp}>
          <HelpPopupInline
            topic="pop_DMC_SmartPages_CustomerActionButton"
            ariaLabel={
              i18n.getString(_TL_('More information about {{item}}'), { item: ctaLabel })
            }
          />
        </div>
      </h2>
      <div className={descriptionClassName}>
        {i18n.getString(_TL_('Select the primary action you want customers to take on your website.'))}
      </div>
      <div className={editorContentClassName}>
        <Dropdown
          className="smart-action-button-type-dropdown"
          selectedKey={selectedActionType ? selectedActionType.key : undefined}
          label={i18n.getString(_TL_('Customer action'))}
          placeholder={i18n.getString(_TL_('Select customer action button'))}
          options={options}
          onChange={onActionTypeChange}
          {...accessibilityProps}
        />
        <ActionCustomEditor
          i18n={i18n}
          classes={classes}
          pilots={pilots}
          selectedActionType={selectedActionType}
          dispatchSiteConfig={dispatchSiteConfig}
          {...actionButtonProps}
        />
      </div>
    </div>
  );
};

UnstyledSmartActionButtonEditor.propTypes = {
  classes: PropTypes.shape({
    group: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  componentId: PropTypes.string.isRequired,
};

export const SmartActionButtonEditor = withDefaultStyles(
  UnstyledSmartActionButtonEditor,
  getStyles
);
